import React, { useContext, useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import {
    VideoChooserBlock as Value,
    VideoChooserBlockWithOptions,
} from "@reactivated";

import { VideoChooserBlock } from "@thelabnyc/thelabui/src/components/VideoChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ReactivatedContext as Context } from "../../context";
import { Clickable } from "../Clickables";
import { MotionPauseContext } from "../SKSCarousel/MotionPauseContext";
import { Svg } from "../Svg";
import { useDetectAudio } from "./utils";

import styles from "./index.module.scss";

export type PlaybackBehavior =
    | VideoChooserBlockWithOptions["playback_behavior"]
    | "native_controls";

/**
 * Attributes consumers may set on the `<video>` element. Excludes the
 * handlers VideoPlayerControls wires internally to mirror playback state —
 * consumer attrs spread last and would replace them.
 */
export type VideoAttrs = Omit<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    | "onPlay"
    | "onPause"
    | "onLoadStart"
    | "onWaiting"
    | "onPlaying"
    | "onCanPlayThrough"
    | "onVolumeChange"
>;

export const VideoPlayerControls = ({
    value,
    wrapperAttrs,
    attrs,
    playbackBehavior = "autoplay",
}: {
    value: Value;
    wrapperAttrs?: React.HTMLAttributes<HTMLDivElement>;
    attrs?: VideoAttrs;
    playbackBehavior?: PlaybackBehavior;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { LOCALE } = useContext(Context);

    const isAutoplay = playbackBehavior === "autoplay";
    const isNativeControls = playbackBehavior === "native_controls";
    const isPressToPlay = playbackBehavior === "press_to_play";
    const isPressToPlayNative = playbackBehavior === "press_to_play_native";

    // Autoplay videos are preloaded; others load on interaction
    const willPreload = isAutoplay;

    // Tracks whether or not the video loadeddata event has been called yet.
    const [isPlayable, setIsPlayable] = useState(false);

    // Tracks whether or not the video _should_ be playing.
    const [isPlaying, setIsPlaying] = useState(isAutoplay);

    // Tracks whether or not the video is currently loading.
    const [isLoading, setIsLoading] = useState(willPreload);

    // Tracks whether or not the video is currently visible in the viewport.
    const [isVisible, setIsVisible] = useState(false);

    // Tracks whether or not the video should have sources yet.
    const [loadVideoSources, setLoadVideoSources] = useState(false);

    // Tracks whether the video is currently muted (autoplay videos start muted)
    const [isMuted, setIsMuted] = useState(isAutoplay);

    // Tracks whether playback has ever started, so press_to_play_native swaps to
    // native controls exactly once. Keying off isPlaying would bring the centered
    // play button back on pause, covering the native bar with its full-surface hitbox.
    const [hasStarted, setHasStarted] = useState(false);

    // When externally controlled (e.g. inside a hero carousel), pause/resume
    // the video based on the carousel's unified pause button.
    //
    // State-only on purpose: the isPlaying-sync effect below is the single
    // place that imperatively touches the <video>. A second imperative
    // driver races it across effect passes (stale isPlaying) and can
    // permanently stick an autoplay video paused on its first frame.
    const isMotionPaused = useContext(MotionPauseContext);
    const isExternallyControlled = isMotionPaused !== null;
    useEffect(() => {
        if (!isExternallyControlled || !isAutoplay || !videoRef.current) return;
        if (isMotionPaused) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors the external motion-pause control into playback state
            setIsPlaying(false);
        } else if (isPlayable && isVisible) {
            setIsPlaying(true);
        }
    }, [
        isMotionPaused,
        isExternallyControlled,
        isAutoplay,
        isPlayable,
        isVisible,
    ]);

    const toggleIsPlaying = () => {
        // Ensure sources are loaded when user initiates playback
        if (!loadVideoSources) {
            setLoadVideoSources(true);
        }
        // Show loader if video isn't ready yet
        if (!isPlaying && !isPlayable) {
            setIsLoading(true);
        }
        setIsPlaying((isPlaying) => !isPlaying);
    };

    /**
     * When the video first becomes visible, load the sources.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isVisible) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- one-way "load sources once visible" latch; depends on runtime visibility, not derivable during render
            setLoadVideoSources(true);
        }
        /**
         * Manually set visibility of subtitles. If a track matches the active
         * locale it should be on by default, unless it's English. All other
         * tracks should be hidden.
         *
         * This overrides default browser behavior which seems to favor turning
         * on a subtitle by default if it's there as well as using the browser's/
         * system's language setting to determine the track.
         */
        [...videoRef.current.textTracks].forEach((track) => {
            const trackLanguage = track.language.substring(0, 2);
            const activeLanguage = LOCALE.substring(0, 2);
            if (trackLanguage === activeLanguage && trackLanguage !== "en") {
                track.mode = "showing";
            } else {
                track.mode = "hidden";
            }
        });
    }, [isVisible]);

    /**
     * If this is an autoplay video, play it as soon as it's visible and ready,
     * unless an external controller currently says "paused". `isLoading` is
     * deliberately not a gate: loading is transient, `play()` on a
     * still-loading video safely pends, and "pause because loading" can race
     * the MotionPauseContext effect above into sticking the video paused.
     */
    useEffect(() => {
        if (!videoRef.current || !isAutoplay) {
            return;
        }
        const isExternallyPaused = isExternallyControlled && isMotionPaused;
        if (isVisible && isPlayable && !isExternallyPaused) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs autoplay playback to runtime visibility/playable state
            setIsPlaying(true);
        } else if (!isVisible) {
            setIsPlaying(false);
        }
    }, [
        isAutoplay,
        isVisible,
        isPlayable,
        isExternallyControlled,
        isMotionPaused,
    ]);

    /**
     * When the isPlaying state changes, update the DOM state to match.
     * For autoplay, we wait for isPlayable (video data loaded).
     * For user-initiated, we call play() immediately to trigger loading.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isPlaying && (isPlayable || !isAutoplay)) {
            videoRef.current.play().catch(() => {
                setIsPlaying(false);
            });
        } else if (!isPlaying) {
            void videoRef.current.pause();
        }
    }, [isPlaying, isPlayable, isAutoplay]);

    /**
     * Wait until after everything else is done loading to add sources to the
     * video element for page load/performance reasons.
     */
    useEffect(() => {
        const contentLoaded = () => {
            if (document.readyState === "complete") {
                setLoadVideoSources(true);
            }
        };
        document.addEventListener("readystatechange", contentLoaded);
        return () => {
            document.removeEventListener("readystatechange", contentLoaded);
        };
    }, []);

    /**
     * When the video element finishes loading metadata, set the playable boolean.
     */
    useEffect(() => {
        const setPlayable = () => {
            setIsPlayable(true);
        };
        const videoElem = videoRef.current;
        if (videoElem) {
            videoElem.addEventListener("loadeddata", setPlayable);
        }
        return () => {
            if (videoElem) {
                videoElem.removeEventListener("loadeddata", setPlayable);
            }
        };
    }, []);

    /**
     * Track if the video element is visible or not, and update the corresponding state bool.
     */
    useEffect(() => {
        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (!(entry.target instanceof HTMLVideoElement)) return;
                setIsVisible(entry.isIntersecting);
            });
        };
        const observer = new IntersectionObserver(callback, {
            threshold: 0.1,
        });
        const videoElem = videoRef.current;
        if (videoElem) {
            observer.observe(videoElem);
        }
        return () => {
            if (videoElem) {
                observer.unobserve(videoElem);
            }
        };
    }, []);

    const modifiedValue = {
        ...value,
        sources: loadVideoSources ? value.sources : [],
    };

    const hasAudio = useDetectAudio(videoRef, modifiedValue.sources);

    return (
        <div
            {...wrapperAttrs}
            className={concatClassNames([
                styles.root,
                wrapperAttrs?.className || "",
            ])}
            style={{ aspectRatio: modifiedValue.aspectRatio || "16/9" }}
        >
            {isLoading && (
                <>
                    <div className={styles.loaderOverlay} aria-hidden="true" />
                    <div className={styles.loaderContainer} aria-hidden="true">
                        <div className={styles.loader} />
                    </div>
                </>
            )}
            <VideoChooserBlock
                ref={videoRef}
                iOSFriendlyMutedAutoPlay={isAutoplay}
                attrs={{
                    loop: isAutoplay,
                    controls:
                        isNativeControls || (isPressToPlayNative && hasStarted),
                    preload: willPreload ? "auto" : "none",
                    onLoadStart: () => {
                        // When preload is set to "none", this still gets called
                        // right away, but none of the "loading finished" events
                        // will get called (since nothing is actually loading yet).
                        // Thus, only get isLoading is preload is enabled.
                        if (willPreload) {
                            setIsLoading(true);
                        }
                    },
                    onWaiting: () => {
                        setIsLoading(false);
                    },
                    onPlaying: () => {
                        setIsLoading(false);
                    },
                    onCanPlayThrough: () => {
                        setIsLoading(false);
                    },
                    muted: isMuted,
                    onPause: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                    },
                    onPlay: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                        setHasStarted(true);
                    },
                    onVolumeChange: (e) => {
                        setIsMuted(e.currentTarget.muted);
                    },
                    ...attrs,
                }}
                value={modifiedValue}
            />
            {((isPressToPlay && !isPlaying) ||
                (isPressToPlayNative && !hasStarted)) && (
                <Clickable
                    disabled={isLoading}
                    onPress={toggleIsPlaying}
                    className={styles.centeredPlayButtonContainer}
                >
                    <VisuallyHidden>{t`Play Video`}</VisuallyHidden>
                    <div className={styles.centeredPlayButton}>
                        <Svg
                            name="play"
                            aria-hidden="true"
                            className={styles.centeredPlayIcon}
                        />
                    </div>
                </Clickable>
            )}
            {!isNativeControls &&
                !isPressToPlayNative &&
                (isAutoplay || isPlaying) && (
                    <div className={styles.controlBar}>
                        {hasAudio && (
                            <Clickable
                                disabled={isLoading}
                                onPress={() => setIsMuted(!isMuted)}
                                className={styles.muteControl}
                            >
                                <VisuallyHidden>
                                    {isMuted ? t`Unmute Video` : t`Mute Video`}
                                </VisuallyHidden>
                                <Svg
                                    name={isMuted ? "audio-off" : "audio-on"}
                                    aria-hidden="true"
                                    className={styles.autoPlaySvg}
                                />
                            </Clickable>
                        )}
                        <Clickable
                            disabled={isLoading || isExternallyControlled}
                            onPress={toggleIsPlaying}
                            className={styles.playControl}
                            style={
                                isExternallyControlled
                                    ? { visibility: "hidden" }
                                    : undefined
                            }
                            aria-hidden={isExternallyControlled || undefined}
                        >
                            <VisuallyHidden>
                                {isPlaying ? t`Pause Video` : t`Play Video`}
                            </VisuallyHidden>
                            <Svg
                                name={isPlaying ? "pause" : "play"}
                                aria-hidden="true"
                                className={styles.autoPlaySvg}
                            />
                        </Clickable>
                    </div>
                )}
        </div>
    );
};
